/**
 * Playing-card art, drawn programmatically as small pixelated canvases — one source
 * of truth for the blackjack table overlay (sibling to `itemArt.ts`). No binary
 * assets; swap for real sprites later by changing only this file.
 *
 * A card code is rank+suit, e.g. "AS", "TD". `T` renders as "10".
 */

export const CARD_W = 60;
export const CARD_H = 84;

const SUIT_GLYPH: Record<string, string> = { S: "♠", H: "♥", D: "♦", C: "♣" };
const RED = "#c41f2b";   // hearts + diamonds render red
const BLACK = "#1a1526"; // spades + clubs stay dark

function rankLabel(rank: string): string {
  return rank === "T" ? "10" : rank;
}

/** Draw a face-up card (rank+suit code) into a fresh pixelated canvas. */
export function drawCard(code: string): HTMLCanvasElement {
  const canvas = blank();
  const ctx = canvas.getContext("2d")!;
  const rank = code[0];
  const suit = code[1];
  const color = suit === "H" || suit === "D" ? RED : BLACK;
  const label = rankLabel(rank);
  const glyph = SUIT_GLYPH[suit] ?? "?";

  // Corner rank (top-left) + suit pip.
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  ctx.font = "bold 18px ui-monospace, monospace";
  ctx.fillText(label, 4, 4);
  ctx.font = "18px ui-monospace, monospace";
  ctx.fillText(glyph, 4, 22);

  // Center suit.
  ctx.font = "32px ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.fillText(glyph, CARD_W / 2, CARD_H / 2 - 16);
  ctx.textAlign = "left";
  return canvas;
}

/** Draw a face-down card (the dealer's hole during play). */
export function drawCardBack(): HTMLCanvasElement {
  const canvas = blank("#0b0b0b");
  const ctx = canvas.getContext("2d")!;
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  for (let i = -CARD_H; i < CARD_W; i += 8) {
    ctx.beginPath();
    ctx.moveTo(i + 0.5, 0);
    ctx.lineTo(i + CARD_H + 0.5, CARD_H);
    ctx.stroke();
  }
  return canvas;
}

function blank(bg = "#f4f1ea"): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  // Card body + dark rim.
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  ctx.fillStyle = bg;
  ctx.fillRect(2, 2, CARD_W - 4, CARD_H - 4);
  return canvas;
}
